/**
 * Oct 20 (FD-06 / FD-07) — repairs whose vehicle left before the work was finished, still outstanding.
 *
 * Reads ticket_incomplete_pickups (RLS: members of the shop) joined to the ticket. Two outstanding states (Oct 20
 * addendum L3 / L6 — the scheduler's `__repair_departure_state`):
 *   awaiting_return               the ticket is still in the incomplete-pickup end state (Blocked, picked_up_at set)
 *   returned_awaiting_scheduling  checked back in (returned_at set) and no continuation yet — the front office's
 *                                 to schedule; this list (not a stale-work alert) is where it surfaces
 * A ticket later closed / deleted, or a repair whose continuation exists, drops out on its own. The expected return
 * date is shown as the record says — informational, never an appointment.
 *
 * Shares the Workboard's query-key prefix (['foh-custody-queue', shopId]) so every existing invalidation path
 * (custody realtime, Dayboard broadcast, pickup convergence) refreshes it too.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface OutstandingIncompletePickup {
  ticketId: string;
  ticketNumber: number | null;
  customerName: string | null;
  vehicle: string | null;
  reason: string;
  expectedReturnDate: string | null;
  pickedUpAt: string;
  recordedBy: string;
  state: 'awaiting_return' | 'returned_awaiting_scheduling';
  returnedAt: string | null;
}

export const outstandingIncompletePickupsKey = (shopId: string) => ['foh-custody-queue', shopId, 'incomplete-pickups'] as const;

interface Row {
  ticket_id: string; reason: string; expected_return_date: string | null; picked_up_at: string; recorded_by: string;
  returned_at?: string | null; return_continuation_ticket_id?: string | null;
  ticket: {
    simple_ticket_number: number | null; customer_name: string | null; status: string | null;
    picked_up_at: string | null; deleted_at: string | null;
    vehicle: { year: number | null; make: string | null; model: string | null } | null;
  } | null;
}

/** Latest record per ticket, only while the ticket is still the outstanding incomplete repair. */
export function outstandingFromRows(rows: readonly Row[]): OutstandingIncompletePickup[] {
  const latest = new Map<string, Row>();
  for (const r of rows) {
    const t = r.ticket;
    if (!t || t.deleted_at) continue;
    const awaiting = t.status === 'Blocked' && !!t.picked_up_at && !r.returned_at;
    const returnedUnscheduled = !!r.returned_at && !r.return_continuation_ticket_id && !t.picked_up_at;
    if (!awaiting && !returnedUnscheduled) continue;
    const prev = latest.get(r.ticket_id);
    if (!prev || prev.picked_up_at < r.picked_up_at) latest.set(r.ticket_id, r);
  }
  return [...latest.values()]
    .sort((a, b) => (a.expected_return_date ?? '9999').localeCompare(b.expected_return_date ?? '9999')
      || b.picked_up_at.localeCompare(a.picked_up_at))
    .map((r) => ({
      ticketId: r.ticket_id,
      ticketNumber: r.ticket?.simple_ticket_number ?? null,
      customerName: r.ticket?.customer_name ?? null,
      vehicle: [r.ticket?.vehicle?.year, r.ticket?.vehicle?.make, r.ticket?.vehicle?.model].filter(Boolean).join(' ') || null,
      reason: r.reason,
      expectedReturnDate: r.expected_return_date,
      pickedUpAt: r.picked_up_at,
      recordedBy: r.recorded_by,
      state: r.returned_at ? 'returned_awaiting_scheduling' as const : 'awaiting_return' as const,
      returnedAt: r.returned_at ?? null,
    }));
}

export function useOutstandingIncompletePickups(shopId: string, enabled = true) {
  return useQuery({
    queryKey: outstandingIncompletePickupsKey(shopId),
    enabled: enabled && !!shopId,
    retry: false,
    queryFn: async (): Promise<OutstandingIncompletePickup[]> => {
      // OCT20A WS-BROWSER2: the table has TWO foreign keys to tickets since the addendum (ticket_id and
      // return_continuation_ticket_id), so a bare `tickets!inner` embed is ambiguous (PostgREST PGRST201) and the whole
      // read failed — the outstanding list (and its "Vehicle returned — check in") never rendered. Name the FK.
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('ticket_incomplete_pickups')
        .select('ticket_id, reason, expected_return_date, picked_up_at, recorded_by, returned_at, return_continuation_ticket_id, '
          + 'ticket:tickets!ticket_incomplete_pickups_ticket_id_fkey!inner(simple_ticket_number, customer_name, status, picked_up_at, deleted_at, vehicle:vehicles(year, make, model))')
        .eq('shop_id', shopId)
        .order('picked_up_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return outstandingFromRows((data ?? []) as Row[]);
    },
  });
}
